import { createReservoir, Reservoir } from './reservoir';

export type PointerKind = 'mouse' | 'touch' | 'pen' | 'none';

export interface MetricsReading {
  pointer: {
    byType: Record<string, number>;
    moves: number;
    pathPx: number;
    segments: number;
    straightness: { p50: number | null; p90: number | null };
    dwellMs: { p50: number | null; p90: number | null; max: number | null };
    gapMs: { p50: number | null; p90: number | null };
  };
  scroll: { events: number; maxDepthPct: number; totalPx: number };
  focus: { blurs: number; hiddenMs: number; returns: number };
  timing: {
    firstInteractionMs: number | null;
    lastInteractionMs: number | null;
    visibleMs: number;
  };
  viewport: { w: number; h: number; dpr: number };
  primaryPointer: PointerKind;
  interactionCount: number;
  firstInteractionMs: number | null;
  elapsedMs: number;
}

export interface Metrics {
  attach(target: Document): () => void;
  read(): MetricsReading;
  dirty(): boolean;
  markClean(): void;
}

/** A gesture ends once the pointer has been still for this long. */
const GESTURE_IDLE_MS = 500;
/** At most one kept sample per this many ms — the page must not pay for the raw event rate. */
const MOVE_SAMPLE_MS = 40;

const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * Running aggregates of how the visit was handled.
 *
 * Every listener is passive and updates counters in O(1); nothing here reads layout, allocates per
 * event or keeps a trajectory. What leaves this module is a distribution — never a path that could
 * be replayed, and never anything the client typed or copied.
 *
 * Straightness is measured per gesture, not per visit: over a whole visit the straight-line distance
 * between the first and last sample says nothing, because a client who moves to the copy button and
 * later to the upload button has a near-zero ratio while both movements were perfectly ordinary.
 */
export const createMetrics = (now: () => number = () => Date.now()): Metrics => {
  const startedAt = now();
  const byType: Record<string, number> = {};
  const dwell: Reservoir = createReservoir();
  const gap: Reservoir = createReservoir();
  const straightness: Reservoir = createReservoir();

  let moves = 0;
  let pathPx = 0;
  let segments = 0;
  let scrollEvents = 0;
  let scrollPx = 0;
  let maxDepthPct = 0;
  let blurs = 0;
  let returns = 0;
  let hiddenMs = 0;
  let hiddenAt: number | null = null;
  let interactions = 0;
  let firstInteractionMs: number | null = null;
  let lastInteractionMs: number | null = null;
  let changed = false;

  let downAt: number | null = null;
  let lastMoveAt: number | null = null;
  let lastX = 0;
  let lastY = 0;
  let lastScrollY = 0;
  let segmentPathPx = 0;
  let segmentStartX = 0;
  let segmentStartY = 0;
  let inSegment = false;

  const touched = (): void => {
    const at = now() - startedAt;
    interactions += 1;
    if (firstInteractionMs === null) firstInteractionMs = at;
    lastInteractionMs = at;
    changed = true;
  };

  const closeSegment = (): void => {
    if (!inSegment) return;
    inSegment = false;
    segments += 1;
    if (segmentPathPx > 0) {
      const dx = lastX - segmentStartX;
      const dy = lastY - segmentStartY;
      straightness.add(Math.sqrt(dx * dx + dy * dy) / segmentPathPx);
    }
    segmentPathPx = 0;
  };

  const onDown = (e: Event): void => {
    const p = e as PointerEvent;
    const kind = p.pointerType || 'mouse';
    byType[kind] = (byType[kind] ?? 0) + 1;
    downAt = now();
    touched();
  };

  const onUp = (): void => {
    if (downAt !== null) dwell.add(now() - downAt);
    downAt = null;
    changed = true;
  };

  const onMove = (e: Event): void => {
    const p = e as PointerEvent;
    const at = now();
    if (lastMoveAt !== null) {
      const since = at - lastMoveAt;
      if (since < MOVE_SAMPLE_MS) return;
      if (since >= GESTURE_IDLE_MS) closeSegment();
      else gap.add(since);
    }

    const x = p.clientX ?? 0;
    const y = p.clientY ?? 0;
    if (!inSegment) {
      inSegment = true;
      segmentStartX = x;
      segmentStartY = y;
    } else {
      const step = Math.sqrt((x - lastX) ** 2 + (y - lastY) ** 2);
      pathPx += step;
      segmentPathPx += step;
    }

    lastX = x;
    lastY = y;
    lastMoveAt = at;
    moves += 1;
    changed = true;
  };

  const onScroll = (): void => {
    const y = window.scrollY ?? 0;
    scrollPx += Math.abs(y - lastScrollY);
    lastScrollY = y;
    scrollEvents += 1;
    const height = document.documentElement?.scrollHeight ?? 0;
    const viewport = window.innerHeight ?? 0;
    if (height > viewport && viewport > 0) {
      maxDepthPct = Math.max(
        maxDepthPct,
        Math.min(100, Math.round(((y + viewport) / height) * 100)),
      );
    }
    changed = true;
  };

  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = now();
      blurs += 1;
    } else if (hiddenAt !== null) {
      hiddenMs += now() - hiddenAt;
      hiddenAt = null;
      returns += 1;
    }
    changed = true;
  };

  return {
    attach(target: Document): () => void {
      const options: AddEventListenerOptions = { passive: true, capture: true };
      target.addEventListener('pointerdown', onDown, options);
      target.addEventListener('pointerup', onUp, options);
      target.addEventListener('pointermove', onMove, options);
      target.addEventListener('visibilitychange', onVisibility, options);
      window.addEventListener('scroll', onScroll, options);

      return () => {
        target.removeEventListener('pointerdown', onDown, options);
        target.removeEventListener('pointerup', onUp, options);
        target.removeEventListener('pointermove', onMove, options);
        target.removeEventListener('visibilitychange', onVisibility, options);
        window.removeEventListener('scroll', onScroll, options);
      };
    },

    read(): MetricsReading {
      closeSegment();
      const elapsedMs = now() - startedAt;
      // A time the tab spent hidden that has not been closed yet still counts: the snapshot that
      // matters most is taken on the way out, while the page is hidden.
      const hidden = hiddenMs + (hiddenAt === null ? 0 : now() - hiddenAt);
      const dominant = Object.entries(byType).sort((a, b) => b[1] - a[1])[0];
      const straight = (q: number): number | null => {
        const value = straightness.quantile(q);
        return value === null ? null : round3(value);
      };

      return {
        pointer: {
          byType: { ...byType },
          moves,
          pathPx: Math.round(pathPx),
          segments,
          straightness: { p50: straight(0.5), p90: straight(0.9) },
          dwellMs: {
            p50: dwell.quantile(0.5),
            p90: dwell.quantile(0.9),
            max: dwell.max(),
          },
          gapMs: { p50: gap.quantile(0.5), p90: gap.quantile(0.9) },
        },
        scroll: {
          events: scrollEvents,
          maxDepthPct,
          totalPx: Math.round(scrollPx),
        },
        focus: { blurs, hiddenMs: Math.round(hidden), returns },
        timing: {
          firstInteractionMs,
          lastInteractionMs,
          visibleMs: Math.max(0, Math.round(elapsedMs - hidden)),
        },
        viewport: {
          w: window.innerWidth ?? 0,
          h: window.innerHeight ?? 0,
          dpr: Math.round((window.devicePixelRatio || 0) * 100) / 100,
        },
        primaryPointer: (dominant?.[0] as PointerKind) ?? 'none',
        interactionCount: interactions,
        firstInteractionMs,
        elapsedMs: Math.round(elapsedMs),
      };
    },

    dirty(): boolean {
      return changed;
    },

    markClean(): void {
      changed = false;
    },
  };
};
